import { Box, Typography, Button, Alert } from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { DialogBox } from '~pages/pages-behind-login/chartering/pre-fixture-page/components/dialog-box'
import { UnsavedChangesCloseDialog } from '~pages/pages-behind-login/chartering/call-log-page/components/unsaved-changes-close-dialog'
import { usePatchPortInfo } from '~hooks/queries/port-info/use-patch-port-info'
import { useDeletePortInfo } from '~hooks/queries/port-info/use-delete-port-info'
import { useGetPortInfoById } from '~hooks/queries/port-info/use-get-port-info'
import type {
  PortInfoItem,
  PortInfoFormData,
  ContinentSelection,
  CountrySelection,
  PortInfoType,
} from '../../types'
import { PageTwoSimple } from '../add-visit/sub-components/page-two/page-two-simple'
import {
  buildEditPatchPayload,
  getMissingRequiredFields,
} from '../../utils/form-utils'
import { COLORS } from '~theme/colors'
import { formatDate } from '~utils/dates'
import { useDateFormat } from '~hooks/use-date-format'

const modalSlotProps = {
  paper: { sx: { width: '90vw', maxWidth: '1200px' } },
}

const DATE_FIELDS = new Set(['dateOfInformation'])
const SKIP_FIELDS = new Set([
  'continentName',
  'continentCode',
  'countryName',
  'countryCode',
])

function entryToFormData(entry: PortInfoItem): PortInfoFormData {
  const formData: PortInfoFormData = {}

  for (const [key, value] of Object.entries(entry)) {
    if (SKIP_FIELDS.has(key)) continue
    if (value === undefined || value === null) continue

    if (DATE_FIELDS.has(key)) {
      const dateKey = key as 'dateOfInformation'
      formData[dateKey] = new Date(value as string)
    } else {
      ;(formData as Record<string, unknown>)[key] = value
    }
  }

  formData.type = entry.type as PortInfoType

  if (
    entry.type === 'continent' &&
    entry.continentName &&
    entry.continentCode
  ) {
    formData.continent = {
      continent: {
        ContinentName: entry.continentName,
        ContinentCode: entry.continentCode,
      },
      continentName: entry.continentName,
    } as ContinentSelection
  }

  if (entry.type === 'country' && entry.countryName && entry.countryCode) {
    formData.country = {
      country: {
        CountryName: entry.countryName,
        CountryCode: entry.countryCode,
        ContinentName: entry.continentName || '',
        ContinentCode: entry.continentCode || '',
      },
      countryName: entry.countryName,
    } as CountrySelection
  }

  return formData
}

interface EditEntryModalProps {
  entryId: string
  onClose: () => void
}

export function EditEntryModal({ entryId, onClose }: EditEntryModalProps) {
  const [formData, setFormData] = useState<PortInfoFormData>({})
  const [showUnsavedCloseConfirm, setShowUnsavedCloseConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [missingFields, setMissingFields] = useState<
    (keyof PortInfoFormData)[]
  >([])
  const initialFormDataRef = useRef<PortInfoFormData | null>(null)
  const { setStatus } = useAnimatedAlert()
  const dateFormat = useDateFormat('with hours and minutes')

  const { data: entry, isLoading: isLoadingEntry } = useGetPortInfoById(entryId)

  const {
    mutate: patchEntry,
    isPending: isPendingPatch,
    isError: isErrorPatch,
  } = usePatchPortInfo()

  const { mutate: deleteEntry, isPending: isPendingDelete } =
    useDeletePortInfo()

  useEffect(() => {
    if (entry) {
      const initialData = entryToFormData(entry)
      setFormData(initialData)
      initialFormDataRef.current = initialData
    }
  }, [entry])

  function hasUnsavedChanges(): boolean {
    if (!initialFormDataRef.current) return false
    return (
      JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current)
    )
  }

  function requestClose() {
    if (hasUnsavedChanges() && !showUnsavedCloseConfirm) {
      setShowUnsavedCloseConfirm(true)
      return
    }
    onClose()
  }

  function onSave() {
    if (!initialFormDataRef.current) return

    const missing = getMissingRequiredFields(formData, '2-simple')
    if (missing.length > 0) {
      setMissingFields(missing)
      setStatus('Please fill in all required fields', undefined, 'error')
      return
    }

    setMissingFields([])
    const payload = buildEditPatchPayload(formData, initialFormDataRef.current)

    patchEntry(
      { portInfoId: entryId, payload },
      {
        onSuccess: () => {
          setStatus('Entry updated successfully', undefined, 'success')
          onClose()
        },
        onError: () => {
          setStatus('Failed to update entry', undefined, 'error')
        },
      },
    )
  }

  function onDelete() {
    deleteEntry(entryId, {
      onSuccess: () => {
        setStatus('Entry deleted successfully', undefined, 'success')
        onClose()
      },
      onError: () => {
        setStatus('Failed to delete entry', undefined, 'error')
      },
    })
  }

  const isInitialLoading = isLoadingEntry && !entry
  const isLoading = isInitialLoading || isPendingPatch || isPendingDelete
  const entryTypeLabel = entry?.type === 'continent' ? 'Continent' : 'Country'

  return (
    <>
      <DialogBox open onClose={requestClose} slotProps={modalSlotProps}>
        <Box
          sx={{
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {isLoading ? (
            <CircularProgress aria-label="Loading…" />
          ) : isErrorPatch ? (
            <Alert severity="error">There was an error saving the entry</Alert>
          ) : (
            <>
              <Box>
                <Typography variant="h3" sx={{ mb: 3 }}>
                  Edit {entryTypeLabel} Entry
                </Typography>
                <PageTwoSimple
                  formData={formData}
                  updateFormData={setFormData}
                  portInfoId={entryId}
                  files={entry?.files}
                  missingFields={missingFields}
                />
              </Box>
              {missingFields.length > 0 && (
                <Typography
                  sx={{ color: COLORS.red.mid, textAlign: 'right', mb: 1 }}
                >
                  Some required fields are not filled
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Entry
                </Button>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Button color="error" onClick={requestClose}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={onSave}>
                    Save
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
        {entry && (
          <Typography
            variant="caption"
            sx={{ color: COLORS.greys.mid, mt: 2 }}
            component="p"
          >
            Last updated by {entry.updatedBy} on{' '}
            {formatDate(entry.updatedAt ?? '', dateFormat)}
          </Typography>
        )}
      </DialogBox>

      {showUnsavedCloseConfirm && (
        <UnsavedChangesCloseDialog
          open={showUnsavedCloseConfirm}
          onConfirmClose={onClose}
          onCancel={() => setShowUnsavedCloseConfirm(false)}
        />
      )}

      {showDeleteConfirm && (
        <DialogBox
          open
          onClose={() => setShowDeleteConfirm(false)}
          slotProps={{ paper: { sx: { maxWidth: '400px' } } }}
        >
          <Typography variant="h4" sx={{ mb: 2 }}>
            Delete Entry?
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Are you sure you want to delete this entry? This action cannot be
            undone.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={onDelete}>
              Delete
            </Button>
          </Box>
        </DialogBox>
      )}
    </>
  )
}
